import Phaser from 'phaser';
import { TILE_SIZE, FARM_SIZE, type FarmPlot } from '../data/world-config';
import { PLANTS } from '../data/plants';
import { RECIPES } from '../data/recipes';
import type { Player } from '../entities/Player';
import type { GameScene } from '../scenes/GameScene';

export class FarmSystem {
  private scene: GameScene;
  private plots: Map<string, FarmPlot> = new Map();
  private plotSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();
  private plantSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();
  private weedSprites: Map<string, Phaser.GameObjects.Arc> = new Map();
  private farmOriginX: number;
  private farmOriginY: number;

  // Object positions for interactive objects
  private wellPos: { x: number; y: number };
  private barnPos: { x: number; y: number };
  private nurseryPos: { x: number; y: number };

  constructor(scene: GameScene, centerX: number, centerY: number) {
    this.scene = scene;
    this.farmOriginX = Math.floor(centerX / TILE_SIZE) - Math.floor(FARM_SIZE / 2);
    this.farmOriginY = Math.floor(centerY / TILE_SIZE) - Math.floor(FARM_SIZE / 2);

    this.wellPos = {
      x: this.farmOriginX + FARM_SIZE + 2,
      y: this.farmOriginY + Math.floor(FARM_SIZE / 2),
    };
    this.barnPos = {
      x: this.farmOriginX - 3,
      y: this.farmOriginY + Math.floor(FARM_SIZE / 2),
    };
    this.nurseryPos = {
      x: this.farmOriginX + Math.floor(FARM_SIZE / 2),
      y: this.farmOriginY - 3,
    };

    this.initializeFarm();
  }

  private initializeFarm(): void {
    const plotAreaSize = 8;
    const offsetX = Math.floor((FARM_SIZE - plotAreaSize) / 2);
    const offsetY = Math.floor((FARM_SIZE - plotAreaSize) / 2);

    for (let y = 0; y < plotAreaSize; y++) {
      for (let x = 0; x < plotAreaSize; x++) {
        const tileX = this.farmOriginX + offsetX + x;
        const tileY = this.farmOriginY + offsetY + y;
        const key = `${tileX},${tileY}`;

        const plot: FarmPlot = {
          x: tileX,
          y: tileY,
          growthStage: 0,
          waterLevel: 0,
          quality: 50,
        };
        this.plots.set(key, plot);

        const sprite = this.scene.add.sprite(
          tileX * TILE_SIZE + TILE_SIZE / 2,
          tileY * TILE_SIZE + TILE_SIZE / 2,
          'terrain',
          24
        );
        sprite.setDepth(1);
        this.plotSprites.set(key, sprite);
      }
    }

    this.addFences();
    this.addFarmObjects();
  }

  private addFences(): void {
    const plotAreaSize = 8;
    const offsetX = Math.floor((FARM_SIZE - plotAreaSize) / 2) - 1;
    const offsetY = Math.floor((FARM_SIZE - plotAreaSize) / 2) - 1;
    const fenceSize = plotAreaSize + 2;

    for (let i = 0; i < fenceSize; i++) {
      const topX = this.farmOriginX + offsetX + i;
      const topY = this.farmOriginY + offsetY;
      this.scene.add.sprite(
        topX * TILE_SIZE + TILE_SIZE / 2,
        topY * TILE_SIZE + TILE_SIZE / 2,
        'terrain', 28
      ).setDepth(2);

      const botY = this.farmOriginY + offsetY + fenceSize - 1;
      this.scene.add.sprite(
        topX * TILE_SIZE + TILE_SIZE / 2,
        botY * TILE_SIZE + TILE_SIZE / 2,
        'terrain', 28
      ).setDepth(2);

      const leftX = this.farmOriginX + offsetX;
      const leftY = this.farmOriginY + offsetY + i;
      this.scene.add.sprite(
        leftX * TILE_SIZE + TILE_SIZE / 2,
        leftY * TILE_SIZE + TILE_SIZE / 2,
        'terrain', 29
      ).setDepth(2);

      const rightX = this.farmOriginX + offsetX + fenceSize - 1;
      this.scene.add.sprite(
        rightX * TILE_SIZE + TILE_SIZE / 2,
        (this.farmOriginY + offsetY + i) * TILE_SIZE + TILE_SIZE / 2,
        'terrain', 29
      ).setDepth(2);
    }
  }

  private addFarmObjects(): void {
    // Water well
    this.scene.add.sprite(
      this.wellPos.x * TILE_SIZE + TILE_SIZE / 2,
      this.wellPos.y * TILE_SIZE + TILE_SIZE / 2,
      'objects', 0
    ).setDepth(5);

    // Storage barn / shop
    this.scene.add.sprite(
      this.barnPos.x * TILE_SIZE + TILE_SIZE / 2,
      this.barnPos.y * TILE_SIZE + TILE_SIZE / 2,
      'objects', 1
    ).setDepth(5);

    // Nursery / combining station
    this.scene.add.sprite(
      this.nurseryPos.x * TILE_SIZE + TILE_SIZE / 2,
      this.nurseryPos.y * TILE_SIZE + TILE_SIZE / 2,
      'objects', 2
    ).setDepth(5);
  }

  interact(tileX: number, tileY: number, player: Player): void {
    // Check if interacting with shop (barn)
    if (this.isNearObject(tileX, tileY, this.barnPos)) {
      this.scene.events.emit('open-shop');
      return;
    }

    // Check if interacting with nursery (combine station)
    if (this.isNearObject(tileX, tileY, this.nurseryPos)) {
      this.tryCombine(player);
      return;
    }

    const key = `${tileX},${tileY}`;
    const plot = this.plots.get(key);
    if (!plot) return;

    const selectedItem = player.getSelectedItem();

    // Weed clearing
    if (plot.hasWeed) {
      this.clearWeed(plot, key);
      return;
    }

    // Sprinkler info
    if (plot.hasSprinkler) {
      this.scene.events.emit('notification', 'Sprinkler is running here.');
      return;
    }

    // Fertilizer application
    if (selectedItem?.plantId === '_fertilizer' && plot.plantId && !plot.fertilized) {
      this.applyFertilizer(plot, player);
      return;
    }

    // Sprinkler placement
    if (selectedItem?.plantId === '_sprinkler' && !plot.plantId) {
      this.placeSprinkler(plot, key, player);
      return;
    }

    // Standard interactions
    if (!plot.plantId) {
      this.plantSeed(plot, key, player);
    } else if (plot.waterLevel < 3) {
      this.waterPlot(plot, key);
    } else if (plot.growthStage >= 3) {
      this.harvest(plot, key, player);
    } else {
      this.scene.events.emit('notification', 'This plant is still growing...');
    }
  }

  private isNearObject(tileX: number, tileY: number, obj: { x: number; y: number }): boolean {
    return Math.abs(tileX - obj.x) <= 1 && Math.abs(tileY - obj.y) <= 1;
  }

  private plantSeed(plot: FarmPlot, key: string, player: Player): void {
    const selectedItem = player.getSelectedItem();
    if (!selectedItem) {
      this.scene.events.emit('notification', 'Select a seed from your inventory!');
      return;
    }

    if (selectedItem.plantId.startsWith('_')) {
      this.scene.events.emit('notification', 'Use this on a planted plot.');
      return;
    }

    const plantSpecies = PLANTS[selectedItem.plantId];
    if (!plantSpecies) return;

    if (!player.removeFromInventory(selectedItem.plantId, 1)) return;

    plot.plantId = selectedItem.plantId;
    plot.growthStage = 0;
    plot.waterLevel = 0;
    plot.plantedAt = Date.now();
    plot.quality = 50;
    plot.fertilized = false;

    const soilSprite = this.plotSprites.get(key);
    if (soilSprite) {
      soilSprite.setFrame(27);
    }

    const plantSprite = this.scene.add.sprite(
      plot.x * TILE_SIZE + TILE_SIZE / 2,
      plot.y * TILE_SIZE + TILE_SIZE / 2,
      'plants',
      plantSpecies.spriteRow * 4 + 0
    );
    plantSprite.setDepth(5);
    this.plantSprites.set(key, plantSprite);

    this.scene.events.emit('notification', `Planted ${plantSpecies.name}!`);
    this.scene.events.emit('farm-planted', { plantId: plot.plantId, tileX: plot.x, tileY: plot.y });
  }

  private waterPlot(plot: FarmPlot, key: string): void {
    plot.waterLevel = Math.min(5, plot.waterLevel + 1);

    // Watering improves quality
    if (plot.quality !== undefined) {
      plot.quality = Math.min(100, plot.quality + 3);
    }

    const soilSprite = this.plotSprites.get(key);
    if (soilSprite) {
      soilSprite.setFrame(25);
    }

    const splash = this.scene.add.circle(
      plot.x * TILE_SIZE + TILE_SIZE / 2,
      plot.y * TILE_SIZE + TILE_SIZE / 2,
      6, 0x6eb5ff, 0.6
    );
    splash.setDepth(20);
    this.scene.tweens.add({
      targets: splash,
      alpha: 0,
      scaleX: 2,
      scaleY: 2,
      duration: 400,
      onComplete: () => splash.destroy(),
    });

    this.scene.events.emit('notification', 'Watered!');
    this.scene.events.emit('farm-watered', { tileX: plot.x, tileY: plot.y });
  }

  private harvest(plot: FarmPlot, key: string, player: Player): void {
    if (!plot.plantId) return;

    const plantSpecies = PLANTS[plot.plantId];
    if (!plantSpecies) return;

    // Quality-based yield
    const quality = plot.quality ?? 50;
    let yield_: number;
    let qualityLabel: string;

    if (quality >= 80) {
      yield_ = 4 + Math.floor(Math.random() * 3);
      qualityLabel = 'Excellent';
    } else if (quality >= 50) {
      yield_ = 3 + Math.floor(Math.random() * 2);
      qualityLabel = 'Good';
    } else {
      yield_ = 2 + Math.floor(Math.random() * 2);
      qualityLabel = 'Normal';
    }

    player.addToInventory(plot.plantId, yield_);
    player.unlockPlant(plot.plantId);

    const plantSprite = this.plantSprites.get(key);
    if (plantSprite) {
      this.scene.tweens.add({
        targets: plantSprite,
        y: plantSprite.y - 20,
        alpha: 0,
        duration: 300,
        onComplete: () => plantSprite.destroy(),
      });
      this.plantSprites.delete(key);
    }

    const soilSprite = this.plotSprites.get(key);
    if (soilSprite) {
      soilSprite.setFrame(24);
    }

    plot.plantId = undefined;
    plot.growthStage = 0;
    plot.waterLevel = 0;
    plot.plantedAt = undefined;
    plot.quality = 50;
    plot.fertilized = false;

    this.scene.events.emit('notification', `${qualityLabel} harvest: ${yield_}x ${plantSpecies.name}!`);
    this.scene.events.emit('farm-harvested', { tileX: plot.x, tileY: plot.y });
  }

  private applyFertilizer(plot: FarmPlot, player: Player): void {
    if (!player.removeFromInventory('_fertilizer', 1)) return;

    plot.fertilized = true;

    const px = plot.x * TILE_SIZE + TILE_SIZE / 2;
    const py = plot.y * TILE_SIZE + TILE_SIZE / 2;
    for (let i = 0; i < 4; i++) {
      const spark = this.scene.add.circle(
        px + Phaser.Math.Between(-5, 5),
        py + Phaser.Math.Between(-5, 5),
        2, 0x88ff44, 0.9
      );
      spark.setDepth(20);
      this.scene.tweens.add({
        targets: spark,
        y: spark.y - 8,
        alpha: 0,
        duration: 500,
        delay: i * 80,
        onComplete: () => spark.destroy(),
      });
    }

    this.scene.events.emit('notification', 'Fertilizer applied! 2x growth speed!');
  }

  private placeSprinkler(plot: FarmPlot, key: string, player: Player): void {
    if (!player.removeFromInventory('_sprinkler', 1)) return;

    plot.hasSprinkler = true;

    const soilSprite = this.plotSprites.get(key);
    if (soilSprite) {
      soilSprite.setFrame(26);
    }

    const sprSpr = this.scene.add.circle(
      plot.x * TILE_SIZE + TILE_SIZE / 2,
      plot.y * TILE_SIZE + TILE_SIZE / 2,
      4, 0x6eb5ff, 0.8
    );
    sprSpr.setDepth(6);
    sprSpr.setStrokeStyle(1, 0x4a90d9);

    this.scene.events.emit('notification', 'Sprinkler placed! Auto-waters nearby plots.');
  }

  private clearWeed(plot: FarmPlot, key: string): void {
    plot.hasWeed = false;

    const weedSprite = this.weedSprites.get(key);
    if (weedSprite) {
      this.scene.tweens.add({
        targets: weedSprite,
        alpha: 0,
        scaleX: 0.3,
        scaleY: 0.3,
        duration: 200,
        onComplete: () => weedSprite.destroy(),
      });
      this.weedSprites.delete(key);
    }

    this.scene.events.emit('notification', 'Weed cleared!');
  }

  /** Spawn a weed on a random empty plot. Called by PlantGrowthSystem. */
  spawnWeed(): void {
    const emptyPlots: { key: string; plot: FarmPlot }[] = [];
    this.plots.forEach((plot, key) => {
      if (!plot.plantId && !plot.hasWeed && !plot.hasSprinkler) {
        emptyPlots.push({ key, plot });
      }
    });

    if (emptyPlots.length === 0) return;

    const { key, plot } = emptyPlots[Math.floor(Math.random() * emptyPlots.length)];
    plot.hasWeed = true;

    const weedSprite = this.scene.add.circle(
      plot.x * TILE_SIZE + TILE_SIZE / 2,
      plot.y * TILE_SIZE + TILE_SIZE / 2 + 2,
      3, 0x4a5a2a, 0.9
    );
    weedSprite.setDepth(4);
    weedSprite.setStrokeStyle(1, 0x3a4a1a);
    this.weedSprites.set(key, weedSprite);
  }

  /** Auto-water plots adjacent to sprinklers. Called by PlantGrowthSystem. */
  processSprinklers(): void {
    this.plots.forEach((plot) => {
      if (!plot.hasSprinkler) return;

      const neighbors = [
        `${plot.x},${plot.y - 1}`,
        `${plot.x},${plot.y + 1}`,
        `${plot.x - 1},${plot.y}`,
        `${plot.x + 1},${plot.y}`,
      ];

      for (const nKey of neighbors) {
        const neighbor = this.plots.get(nKey);
        if (neighbor && neighbor.plantId && neighbor.waterLevel < 4) {
          neighbor.waterLevel = Math.min(5, neighbor.waterLevel + 0.5);

          const soilSprite = this.plotSprites.get(nKey);
          if (soilSprite && neighbor.plantId) {
            soilSprite.setFrame(25);
          }
        }
      }
    });
  }

  /** Check if a plot has adjacent weeds (reduces growth). */
  hasAdjacentWeed(tileX: number, tileY: number): boolean {
    const neighbors = [
      `${tileX},${tileY - 1}`,
      `${tileX},${tileY + 1}`,
      `${tileX - 1},${tileY}`,
      `${tileX + 1},${tileY}`,
    ];
    return neighbors.some(k => this.plots.get(k)?.hasWeed);
  }

  /** Try to combine two plants from the player's inventory. */
  tryCombine(player: Player): void {
    const inventory = player.inventory;

    for (const recipe of RECIPES) {
      const has1 = inventory.find(i => i.plantId === recipe.plant1 && i.quantity >= 1);
      const has2 = inventory.find(i => i.plantId === recipe.plant2 && i.quantity >= 1);

      if (has1 && has2) {
        player.removeFromInventory(recipe.plant1, 1);
        player.removeFromInventory(recipe.plant2, 1);
        player.addToInventory(recipe.result.id, 1);
        player.unlockPlant(recipe.result.id);

        this.scene.events.emit('notification',
          `Combined! Created ${recipe.resultName}!`
        );

        // Celebration sparkles
        const playerX = this.scene.player.sprite.x;
        const playerY = this.scene.player.sprite.y;
        for (let i = 0; i < 6; i++) {
          const sparkle = this.scene.add.circle(
            playerX + Phaser.Math.Between(-12, 12),
            playerY + Phaser.Math.Between(-12, 12),
            2, 0xffdd44, 0.9
          );
          sparkle.setDepth(20);
          this.scene.tweens.add({
            targets: sparkle,
            y: sparkle.y - 15,
            alpha: 0,
            duration: 700,
            delay: i * 60,
            onComplete: () => sparkle.destroy(),
          });
        }
        return;
      }
    }

    this.scene.events.emit('notification', 'No valid combinations. Need 2 different plant types.');
  }

  /** Buy an item from the shop. */
  buyItem(itemId: string, cost: number, player: Player): boolean {
    if (!this.scene.combatSystem) return false;

    if (this.scene.combatSystem.playerCoins < cost) {
      this.scene.events.emit('notification', 'Not enough coins!');
      return false;
    }

    this.scene.combatSystem.playerCoins -= cost;
    player.addToInventory(itemId, itemId === '_fertilizer' ? 3 : 1);
    if (!itemId.startsWith('_')) {
      player.unlockPlant(itemId);
    }

    const name = itemId.startsWith('_') ? itemId.slice(1) : PLANTS[itemId]?.name ?? itemId;
    this.scene.events.emit('notification', `Bought ${name}!`);
    this.scene.events.emit('combat-stats-update', this.scene.combatSystem.getStats());
    return true;
  }

  getPlots(): Map<string, FarmPlot> {
    return this.plots;
  }

  getPlantSprites(): Map<string, Phaser.GameObjects.Sprite> {
    return this.plantSprites;
  }

  isInFarmBounds(tileX: number, tileY: number): boolean {
    return (
      tileX >= this.farmOriginX &&
      tileX < this.farmOriginX + FARM_SIZE &&
      tileY >= this.farmOriginY &&
      tileY < this.farmOriginY + FARM_SIZE
    );
  }
}
